
import apiConfig from '@/config/api.config'
import { PLACEHOLDER_DATA_URI } from '@/assets/placeholder'
import { SPEC_FIELD_MAP, specCategoryName } from '@/utils/specFieldMap'
import { fetchPublicConfig } from '@/utils/publicConfig'
import { resolveProductImageUrl } from '@/utils/image'
// 只读复用字段名→中文label映射（不改动 specFieldMap 本身）
const SPEC_LABELS = Object.values(SPEC_FIELD_MAP)
  .reduce((m, fields) => { fields.forEach(f => { m[f.key] = f.label }); return m }, {})

export default {
  name: 'SearchResult',
  data () {
    return {
      sValue: '',
      isDisplay: false, // 返回顶部按钮显现（滚过 320px；详情页/热卖同阈值）
      showTierBadge: true, // "惠/靓/正"角标全局开关（public config 下发；默认显示）
      searchType: 'keyword', // keyword, spec, car, quick
      // 键必须显式初始化：空对象上后加属性是 Vue2 非响应式赋值，
      // conditionValue computed 依赖收集失败 → 同路由 query 变化时条件标签冻结旧值
      searchParams: { spec: null, car: null, keyword: '' },
      productInfo: [],
      searchEnd: false,
      orderCount: 0,
      defaultImage: PLACEHOLDER_DATA_URI,
      throttledScroll: null,
      // 搜索结果分页加载（最多3页，30个结果）
      currentPage: 1,
      pageSize: 30, // 30×3 增量加载：首批 30，下拉触底各再取 30，自动探索上限 90（= maxPages × pageSize）
      maxPages: 3,
      loadingMore: false,
      hasMore: true,
      searchKey: '', // 当前查询条件标识（变化时清空结果重新搜索）
      // 结果内筛选（result-driven facet）：候选=当前结果集统计（/products 响应 facets），
      // 点击=car_search/spec_search 参数叠加重新请求，不跳转 /search
      facets: { model: [], spec: {} },
      activeFacets: { model: null, spec: {} }, // 车型单值 + 每规格字段单值
      showFacetPanel: false
    }
  },
  created () {
    // 添加滚动监听，使用节流
    this.throttledScroll = this.throttle(this.handleScroll, 200)
    window.addEventListener('scroll', this.throttledScroll)
    // 获取购物车数量
    this.fetchCartCount()
    // "惠/靓/正"角标全局开关（默认 true，显式 false 才隐藏）
    fetchPublicConfig().then(cfg => { this.showTierBadge = cfg.showHuiliangzhengBadge !== false })
  },
  watch: {
    // route query = 搜索状态唯一来源：同路由 query 变化（/result→/result）组件复用，
    // mounted 不再触发，这里补初始化（searchKey 变化自动清结果重搜，幂等）；
    // 离开 /result（path 变）组件即将销毁，不处理
    '$route' (to, from) {
      if (to.path === from.path) this.initSearch()
    }
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.throttledScroll)
  },
  computed: {
    leftProducts () { return this.productInfo.filter((_, i) => i % 2 === 0) },
    rightProducts () { return this.productInfo.filter((_, i) => i % 2 === 1) },
    hasActiveFacet () {
      return !!this.activeFacets.model || Object.keys(this.activeFacets.spec).length > 0
    },
    facetModelOptions () { return this.facets.model || [] },
    facetSpecGroups () { return Object.entries(this.facets.spec || {}) },
    searchPlaceholder () {
      return this.sValue || '搜索商品'
    },
    conditionLabel () {
      const labels = {
        spec: '按规格筛选',
        car: '按车型筛选',
        quick: '快速搜索'
      }
      return labels[this.searchType] || ''
    },
    conditionValue () {
      if (this.searchType === 'spec' && this.searchParams.spec) {
        // 通用字段摘要（旧版写死 tire/battery 四字段，其余类目规格不显示）
        const parts = Object.keys(this.activeFacets.spec).map(k => {
          const label = SPEC_LABELS[k]
          const v = this.activeFacets.spec[k]
          return label ? `${label} ${v}` : String(v)
        })
        return parts.length > 0
          ? `${this.searchParams.spec.categoryName} · ${parts.join(' / ')}`
          : this.searchParams.spec.categoryName
      }
      if (this.searchType === 'car') {
        return (this.searchParams.car && this.searchParams.car.name) || '全部商品'
      }
      if (this.searchType === 'quick') {
        return this.searchParams.keyword || ''
      }
      return ''
    },
    // 用户角色 - 使用 store getter
    userRole () {
      return this.$store.getters.userRole
    },
    isAdmin () {
      return this.$store.getters.isAdmin
    },
    isSuperAdmin () {
      return this.$store.getters.isSuperAdmin
    },
    isCourier () {
      return this.$store.getters.isCourier
    }
  },
  mounted () {
    this.initSearch()
    this.checkOrderCount()
  },
  methods: {
    // 获取商品缩略图（统一 utils/image 归一：正式排序 [0]，不再页面自 parse）
    getProductThumb (product) {
      const url = resolveProductImageUrl(product)
      return (url && this.$img(url)) || this.defaultImage
    },
    handleImageError (event) {
      event.target.src = this.defaultImage
    },
    async initSearch () {
      // 移动 WebView（微信 X5 / iOS WKWebView）下 vue-router scrollBehavior 不可靠：
      // 进结果页/同路由 query 变化都自证回顶（旧条件的浏览位置与 scrollTop 不得带入新搜索）。
      // push 归 0；back（savedPosition）时 router 的恢复在 mounted 之后执行，浏览位置仍恢复。
      window.scrollTo(0, 0)
      this.isDisplay = false // 新搜索自证回顶，同步收返回顶部钮（部分 WebView 不派发 scroll 事件）
      const query = this.$route.query
      this.searchType = query.type || 'keyword'

      // facet 面板开合：本组件 route replace（facet 叠加导航，_facetNav 标记）保持展开，
      // 其余来源（新搜索/返回前进）收起
      const keepPanel = this._facetNav
      this._facetNav = false
      this._searchLogged = false
      this.showFacetPanel = keepPanel ? this.showFacetPanel : false

      // 叠加态一律从 route query 重建（route = 筛选状态唯一真值；back/forward 同路径恢复）
      this.activeFacets = { model: null, spec: {} }
      this.searchParams.spec = null
      this.searchParams.car = null
      this.searchParams.keyword = ''

      // spec query 的字段集解析（剥离 category/categoryName 元键与空值，仅留规格字段）
      const parseSpecFields = (raw) => {
        if (!raw) return {}
        try {
          const p = JSON.parse(raw)
          if (p && typeof p === 'object' && !Array.isArray(p)) {
            const out = {}
            Object.keys(p).forEach(k => {
              if (k !== 'category' && k !== 'categoryName' && p[k] !== '' && p[k] != null) out[k] = p[k]
            })
            return out
          }
        } catch (e) {
          console.error('解析规格参数失败:', e)
        }
        return {}
      }

      if (this.searchType === 'spec') {
        // 规格筛选上下文：category=<key>（类目，后端 p.category 过滤，默认全类目）+
        // spec=<已选字段集 JSON>（可无）。旧协议（spec JSON 内含 category/categoryName，
        // 详情页标签/历史栈）兼容提取
        let categoryKey = query.category || null
        let categoryName = query.categoryName || ''
        const specFields = parseSpecFields(query.spec)
        if (query.spec && (!categoryKey || !categoryName)) {
          try {
            const p = JSON.parse(query.spec)
            if (p && typeof p === 'object' && !Array.isArray(p)) {
              if (!categoryKey && p.category) categoryKey = p.category
              if (!categoryName && p.categoryName) categoryName = p.categoryName
            }
          } catch (e) { /* parseSpecFields 已记录 */ }
        }
        this.searchParams.spec = categoryKey
          ? { category: categoryKey, categoryName: categoryName || specCategoryName(categoryKey) }
          // 无类目的纯规格值筛选（详情页规格标签）：仅字段过滤，取消字段后=全部商品
          : { category: null, categoryName: categoryName || '规格筛选' }
        this.activeFacets.spec = specFields
        this.sValue = Object.keys(specFields).length > 0
          ? `${this.searchParams.spec.categoryName} 筛选`
          : this.searchParams.spec.categoryName
      } else if (this.searchType === 'car' && query.car) {
        try {
          this.searchParams.car = JSON.parse(query.car)
          this.sValue = this.searchParams.car.name || ''
          this.activeFacets.model = this.searchParams.car.name || null
        } catch (e) {
          console.error('解析车型参数失败:', e)
        }
      } else if (this.searchType === 'car') {
        // 车型 facet 全部取消后的基态（car 参数已移除）：全部商品
        this.sValue = '全部商品'
      } else if (this.searchType === 'quick') {
        this.searchParams.keyword = query.keyword || query.id
        this.sValue = this.searchParams.keyword
      } else {
        this.sValue = query.id || ''
      }

      // facet 叠加 query 键（model/spec）：keyword 与 spec 来源通用（类目上下文之上叠车型）；
      // car 来源的车型由 car JSON 承载，不读 model 键
      if (this.searchType !== 'car') {
        this.activeFacets.spec = parseSpecFields(query.spec)
        if (query.model) this.activeFacets.model = String(query.model)
      }

      // facet 快照冻结规则（doSearch 消费）：入口导航（新搜索/back/forward）恒刷新快照
      // （车型/带规格入口的预设条件不是叠加，面板需出候选）；仅 facet 导航（keepPanel）
      // 期间按叠加态判定——叠加中冻结旧快照可继续换选，全部取消后恢复刷新
      this._facetDirty = keepPanel ? this.hasActiveFacet : false

      if (this.sValue || this.searchType !== 'keyword') {
        this.hasMore = true
        await this.doSearch()
      } else {
        this.searchEnd = true
      }
    },
    async doSearch () {
      // P0 §9：stale-response guard——新查询发出后自增序号，旧请求的迟到响应按序号丢弃
      const seq = (this._reqSeq = (this._reqSeq || 0) + 1)
      // 当前查询条件标识：搜索来源 + facet 叠加态（任一变化清空结果重搜）
      const base = (this.searchType === 'spec' || this.searchType === 'car') ? 'filter' : 'kw:' + (this.sValue || '')
      const key = base + '|' + JSON.stringify(this.activeFacets)

      // 查询条件变化 → 清空结果，重新搜索
      if (key !== this.searchKey) {
        this.searchKey = key
        this.productInfo = []
        this.searchEnd = false
        this.currentPage = 1
        this.hasMore = true
      }

      // 搜索行为上报（fire-and-forget：热搜榜统计源，失败不影响结果、不阻塞请求、不参与排序）
      // _searchLogged：同一搜索条件只上报一次——翻页与 facet 叠加/清除的重查不重复计数
      if (!this._searchLogged && this.searchType !== 'spec' && this.searchType !== 'car' && this.sValue && this.currentPage === 1) {
        this._searchLogged = true
        const userId = localStorage.getItem('userId')
        this.axios({ url: 'search/log', method: 'post', data: { keyword: this.sValue, userId: userId || undefined } }).catch(() => {})
      }

      // 请求当前页数据
      try {
        const params = {}
        if (this.searchType !== 'spec' && this.searchType !== 'car') {
          params.search = this.sValue
        }
        // facet 叠加：复用既有 car_search / spec_search 过滤参数（后端同为 WHERE 附加条件，可叠加）
        if (this.activeFacets.model) {
          params.car_search = JSON.stringify({ name: this.activeFacets.model })
        }
        if (this.searchType === 'spec' && this.searchParams.spec) {
          // 规格筛选上下文：category=类目过滤（后端 p.category，全类目默认态）+ 已选规格字段
          const specReq = {}
          if (this.searchParams.spec.category) specReq.category = this.searchParams.spec.category
          Object.assign(specReq, this.activeFacets.spec)
          if (Object.keys(specReq).length > 0) params.spec_search = JSON.stringify(specReq)
        } else if (Object.keys(this.activeFacets.spec).length > 0) {
          params.spec_search = JSON.stringify(this.activeFacets.spec)
        }
        params.page = this.currentPage
        params.limit = this.pageSize

        const res = await this.axios({ url: apiConfig.apiList.getProducts, method: 'get', params })
        if (seq !== this._reqSeq) return // 旧响应迟到：已被更新的查询取代，丢弃防覆盖新结果
        if (res.data.code === 200) {
          // facet 面板快照只在"无叠加"查询时更新（叠加后的 facets 会收窄只剩已选值，
          // 更新会导致无法切换选项；取消全部叠加后恢复更新）
          if (!this._facetDirty && res.data.facets) {
            this.facets = res.data.facets
          }
          const newItems = (res.data.data || []).map(item => ({ ...item, quantity: 1 }))
          // 去重：避免已存在的商品重复添加
          const existingIds = new Set(this.productInfo.map(item => item.id))
          const uniqueNewItems = newItems.filter(item => !existingIds.has(item.id))
          this.productInfo = [...this.productInfo, ...uniqueNewItems]
          // 是否还有更多：优先 canonical pagination.total（真实总数），探索上限 = maxPages × pageSize = 90；
          // 无 total 时回退经典判定（返回不足整页 → 尽头）
          const total = res.data.pagination ? res.data.pagination.total : null
          this.hasMore = (total !== null && total !== undefined)
            ? this.productInfo.length < Math.min(total, this.maxPages * this.pageSize)
            : (newItems.length === this.pageSize && this.currentPage < this.maxPages)
        }
      } catch (err) {
        if (seq !== this._reqSeq) return // 旧请求的错误不污染新查询（也不触发冷却）
        // P0 §16：429 不进 mockData（假数据会掩盖限流）、不自动重发——冷却期内 loadmore 直接跳过
        if (err && err.response && err.response.status === 429) {
          this._cooldownUntil = Date.now() + 5000
          this.$q.notify({ message: '请求过于频繁，请稍后再试', color: 'warning', position: 'top', timeout: 2000 })
        } else {
          console.error('搜索失败:', err)
          this.mockData()
        }
      } finally {
        if (seq === this._reqSeq) this.searchEnd = true
      }
    },
    // Fisher-Yates 洗牌
    shuffleArray (arr) {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]]
      }
      return arr
    },
    mockData () {
      // 模拟搜索结果数据
      const mockItems = [
        { id: 1, name: '米其林轮胎 195/65R15', brand: '米其林', specification: '195/65R15 91V', price: 458, stock: 12, image: '' },
        { id: 2, name: '普利司通轮胎 205/55R16', brand: '普利司通', specification: '205/55R16 91V', price: 398, stock: 8, image: '' },
        { id: 3, name: '瓦尔塔电瓶 60Ah', brand: '瓦尔塔', specification: 'L2-400 60Ah', price: 580, stock: 0, image: '' },
        { id: 4, name: '壳牌机油 5W-30 4L', brand: '壳牌', specification: 'HX7 5W-30 4L', price: 268, stock: 25, image: '' },
        { id: 5, name: '博世刹车片 前片', brand: '博世', specification: '前刹车片', price: 188, stock: 6, image: '' },
        { id: 6, name: '美孚机油 0W-40 4L', brand: '美孚', specification: '金美孚 0W-40 4L', price: 398, stock: 15, image: '' }
      ]
      this.productInfo = mockItems
    },
    // 顶部条件标签的 ×：退出当前筛选上下文回搜索页（spec/car 带上下文恢复到对应 tab）
    clearCondition () {
      this.goBackSearch()
    },
    // ===== 结果内 facet 筛选 =====
    specLabel (key) {
      return SPEC_LABELS[key] || key
    },
    toggleFacet (dim, key, value) {
      // route = 筛选状态唯一真值：facet 变化一律改写 query 后 replace，
      // $route watch → initSearch 从 query 重建并重查（back/forward 走同一路径恢复）。
      // 单选 toggle：再点已选值=取消；category（类目上下文）不是 facet，永不被 chip 消掉
      const query = { ...this.$route.query }
      if (dim === 'model') {
        if (this.searchType === 'car') {
          // 车型筛选上下文：切换=改写 car JSON 车型；再点已选=移除 car 参数（全部商品基态）
          if (this.activeFacets.model === value) {
            delete query.car
          } else {
            let car = {}
            try { car = JSON.parse(query.car || '{}') } catch (e) { car = {} }
            car.name = value
            query.car = JSON.stringify(car)
          }
        } else if (this.activeFacets.model === value) {
          delete query.model
        } else {
          query.model = value
        }
      } else {
        const spec = { ...this.activeFacets.spec }
        if (spec[key] === value) delete spec[key]
        else spec[key] = value
        if (Object.keys(spec).length > 0) query.spec = JSON.stringify(spec)
        else delete query.spec
      }
      this._facetNav = true // initSearch 保持面板展开（facet 导航，非新搜索）
      this.$router.replace({ path: '/result', query }).catch(() => {})
    },
    clearFacets () {
      // 清 facet 字段，保留类目/车型上下文参数：spec 来源=回全类目，keyword 来源=纯关键词，
      // car 来源（车型即上下文）=移除 car 参数回全部商品
      const query = { ...this.$route.query }
      delete query.spec
      delete query.model
      if (this.searchType === 'car' && this.activeFacets.model) delete query.car
      this._facetNav = true
      this.$router.replace({ path: '/result', query }).catch(() => {})
    },
    // 回搜索页：以当前结果页条件构造 query（route query 唯一来源，不依赖历史栈残留——
    // 连续搜索 A→B 后 go(-1) 会落回栈里的 A 条目，push 当前条件才不污染）
    goBackSearch () {
      const query = {}
      if (this.searchType === 'spec' && this.searchParams.spec) {
        query.tab = 'spec'
        query.category = this.searchParams.spec.category || undefined
        query.categoryName = this.searchParams.spec.categoryName
        // 已选规格字段带回筛选页（旧协议整包 spec JSON 混 category，新协议字段集独立）
        const fields = { ...this.activeFacets.spec }
        if (Object.keys(fields).length > 0) query.spec = JSON.stringify(fields)
        query.source = 'spec_filter'
        // placeholder 只提示不填 value（详情标签/筛选返回都可继续修改）
        query.placeholder = `搜索 ${this.searchParams.spec.categoryName || '商品'}`
      } else if (this.searchType === 'car' && this.searchParams.car) {
        query.tab = 'car'
        query.car = JSON.stringify(this.searchParams.car)
        query.source = 'spec_filter'
        query.placeholder = `搜索 ${this.searchParams.car.name || '车型'}`
      }
      this.$router.push({ path: '/search', query })
    },
    backClick () {
      // 筛选来源（spec/car）：返回=恢复当前条件的筛选页，禁止 go(-1) 落到栈里旧条件；
      // 普通搜索（keyword）：历史栈返回即正确来源
      if (this.searchType === 'spec' || this.searchType === 'car') {
        this.goBackSearch()
        return
      }
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push('/home')
      }
    },
    toDetails (productId) {
      this.$router.push({
        path: '/details',
        query: { id: productId }
      })
    },
    // 管理员编辑商品
    editProduct (item) {
      this.$router.push(`/product/${item.id}/edit`)
    },
    addToCart (item) {
      const token = localStorage.getItem('token')
      if (!token) {
        this.$q.notify({
          message: '请先登录',
          color: 'warning',
          position: 'top',
          timeout: 1500
        })
        this.$router.push('/')
        return
      }

      // 默认加1个最小单位
      const productId = item.id || item.product_id || item.productId
      if (!productId) {
        this.$q.notify({ message: '商品信息不完整', color: 'negative', position: 'top' })
        return
      }
      this.axios({
        url: apiConfig.apiList.addToCart,
        method: 'post',
        headers: { Authorization: `Bearer ${token}` },
        data: {
          product_id: String(productId),
          quantity: 1,
          unit_type: 'unit',
          unit_name: item.unit || item.unit_name || '个'
        }
      }).then(res => {
        if (res.data.code === 200) {
          this.$q.notify({
            message: `已添加 ${item.name} 到货篮`,
            color: 'positive',
            position: 'top',
            timeout: 1000
          })
          // 加购后唯一真值走 store（refreshCartCount 拉服务端真相）；本地 cartCount 副本已删（spec H）
          this.$store.dispatch('refreshCartCount')
        } else {
          this.$q.notify({
            message: res.data.message || '添加失败',
            color: 'negative',
            position: 'top',
            timeout: 1500
          })
        }
      }).catch(err => {
        console.error('添加到货篮失败:', err)
        this.$q.notify({
          message: '添加失败，请稍后重试',
          color: 'negative',
          position: 'top',
          timeout: 1500
        })
      })
    },
    async fetchCartCount () {
      // 单一真值：拉服务端真相进 store（本地 cartCount 副本已删，spec G-H）
      await this.$store.dispatch('refreshCartCount')
    },
    checkOrderCount () {
      this.orderCount = this.$store.state.orderlist?.length || 0
    },
    goOrder () {
      this.$router.push({ path: '/order' })
    },
    // 节流函数
    throttle (fn, delay) {
      let timer = null
      return function (...args) {
        if (!timer) {
          timer = setTimeout(() => {
            timer = null
            fn.apply(this, args)
          }, delay)
        }
      }
    },
    // 返回顶部（详情页/热卖同款：滚回 0 并立即收按钮）
    goBackTop () {
      document.body.scrollTop = document.documentElement.scrollTop = 0
      this.isDisplay = false
    },
    // 滚动监听
    handleScroll () {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop
      // 返回顶部按钮：滚过 320px 显现（详情页/热卖同阈值）
      this.isDisplay = scrollTop > 320
      const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight
      const clientHeight = document.documentElement.clientHeight || window.innerHeight
      // 距离底部 100px 时触发加载更多
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        this.loadmore()
      }
    },
    // 分页加载更多
    loadmore () {
      if (this.loadingMore || !this.searchEnd || !this.hasMore) return
      // P0 §16：429 后 5s 冷却——滚动触底不再立即循环重发（服务端仍是真正边界）
      if (Date.now() < (this._cooldownUntil || 0)) return

      this.loadingMore = true
      this.currentPage++
      this.doSearch().finally(() => {
        this.loadingMore = false
      })
    }
  }
}
